import { type ServerService } from '@lib/services/server-data'
import { logError, LogErrorMetrics } from '@lib/tracking/felog'
import { gnarClient } from '@lib/tracking/gnar'
import { Cookie, getCookie } from '@lib/utils/cookies'
import { transformToObject } from '@lib/utils/error'

/**
 * Reads the gnar websiteSessionId from cookies (client-side only)
 */
const getWebsiteSessionIdFromCookie = (): string | undefined => {
  return getCookie(Cookie.GnarWebsiteSessionId)
}

export interface SessionDataService {
  getContainerId: () => string | undefined
  getWebsiteSessionId: () => string | undefined
  getContainerIdPromise: () => Promise<string>
  getWebsiteSessionIdPromise: () => Promise<string>
  initializeClientSide: () => void
}

/**
 * Service that provides access to gnar container ID and web session ID
 * Uses SSR data when available, falls back to client-side logic
 */
export class SessionDataServiceImpl implements SessionDataService {
  private containerId?: string
  private websiteSessionId?: string
  private readonly containerIdPromise: Promise<string>
  private readonly websiteSessionIdPromise: Promise<string>

  private resolveContainerId!: (value: string) => void
  private resolveWebsiteSessionId!: (value: string) => void
  private clientSideInitialized = false

  constructor(serverService: ServerService) {
    const serverData = serverService.get()

    this.containerId = serverData?.containerId
    this.websiteSessionId = serverData?.websiteSessionId

    // Always create promise for client-side container ID fetching
    // SSR data is just an optimization for immediate access
    this.containerIdPromise = new Promise<string>(resolve => {
      this.resolveContainerId = resolve

      // If we have SSR data, resolve immediately but still fetch client-side
      if (this.containerId) {
        resolve(this.containerId)
      }
    })

    // Always create promise for client-side website session ID fetching
    // SSR data is just an optimization for immediate access
    this.websiteSessionIdPromise = new Promise<string>(resolve => {
      this.resolveWebsiteSessionId = resolve

      // If we have SSR data, resolve immediately but still fetch client-side
      if (this.websiteSessionId) {
        resolve(this.websiteSessionId)
      }
    })
  }

  getContainerId(): string | undefined {
    return this.containerId
  }

  getWebsiteSessionId(): string | undefined {
    return this.websiteSessionId
  }

  getContainerIdPromise(): Promise<string> {
    // Always return the client-side promise, which may be pre-resolved with SSR data
    return this.containerIdPromise
  }

  getWebsiteSessionIdPromise(): Promise<string> {
    // Always return the client-side promise, which may be pre-resolved with SSR data
    return this.websiteSessionIdPromise
  }

  /**
   * Initialize client-side container ID fetching (should be called on client-side only)
   * Always fetches from client-side to ensure we have the most up-to-date value
   */
  initializeClientSide(): void {
    if (this.clientSideInitialized) {
      return // Already initialized
    }

    this.clientSideInitialized = true

    // Fetch container ID
    gnarClient
      .getContainerId()
      .then(id => {
        this.containerId = id
        // Always resolve with the client-side value, even if we had SSR data
        // This ensures we have the most current container ID
        this.resolveContainerId(id)
      })
      .catch(_error => {
        void logError(`Error getting Gnar container id`, LogErrorMetrics.GnarContainerIdFailed, {
          error: transformToObject(_error as Error),
        })

        // If we had SSR data and client-side fails, keep the SSR value
        // Otherwise, resolve with empty string
        if (!this.containerId) {
          this.resolveContainerId('')
        }
      })

    // Website session ID: read from cookie on client-side, fallback to SSR data
    const cookieWebsiteSessionId = getWebsiteSessionIdFromCookie()
    if (cookieWebsiteSessionId) {
      this.websiteSessionId = cookieWebsiteSessionId
      this.resolveWebsiteSessionId(cookieWebsiteSessionId)
    } else {
      // Fallback to SSR data if no cookie value
      this.resolveWebsiteSessionId(this.websiteSessionId || '')
    }
  }
}
